import { Role } from '@/constants/role';
import { TrajectoryStatus } from '@/constants/trajectory-status';
import { TransportStatus } from '@/constants/transport-status';
import { VehicleStatus } from '@/constants/vehicle-status';
import { AssignmentStatus } from '@/constants/assignment-status';
import AuthLayout from '@/layout/AuthLayout.vue';
import DefaultLayout from '@/layout/DefaultLayout.vue';
import { useAuthStore } from '@/stores/authentication-store';
import { useAxiosStore } from '@/stores/axios-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useTransportRoutingStore } from '@/stores/transport/transport-routing-store';
import { createRouter, createWebHistory } from 'vue-router';
import { RateGroupType } from '@/constants/rate-group-type';

const LoginView = () => import('@/views/authentication/LoginView.vue');
const SsoCallbackView = () => import('@/views/authentication/SsoCallbackView.vue');
const ConsentCallbackView = () => import('@/views/authentication/ConsentCallbackView.vue');
const VehicleListView = () => import('@/views/vehicles/VehicleListView.vue');
const VehicleDetailView = () => import('@/views/vehicles/VehicleDetailView.vue');
const AssignmentListView = () => import('@/views/assignments/AssignmentListView.vue');
const AssignmentView = () => import('@/views/assignments/AssignmentView.vue');
const AssignmentCreateView = () => import('@/views/assignments/AssignmentCreateView.vue');
const OrganizationSettingsView = () => import('@/views/organization/OrganizationSettingsView.vue');
const TransportListView = () => import('@/views/transports/TransportListView.vue');
const TransportView = () => import('@/views/transports/TransportView.vue');
const UsersView = () => import('@/views/user/UserListView.vue');
const NotFoundView = () => import('@/views/NotFoundView.vue');
const PasswordResetRequestView = () => import('@/views/authentication/PasswordResetRequestView.vue');
const PasswordResetRequestSuccess = () => import('@/views/authentication/PasswordResetRequestSuccess.vue');
const CreateNewPasswordView = () => import('@/views/authentication/CreateNewPasswordView.vue');
const NoAccess = () => import('@/views/NoAccessView.vue');
const AdministrationOrganizationView = () => import('@/views/administration/organization/OrganizationListView.vue');
const RelationView = () => import('@/views/relation/RelationListView.vue');
const RelationDetailView = () => import('@/views/relation/RelationDetailView.vue');
const TransportVehicleListView = () => import('@/views/transport-vehicle/TransportVehicleListView.vue');
const TrajectoryListView = () => import('@/views/trajectory/TrajectoryListView.vue');
const TransportReasonsView = () => import('@/views/organization/TransportReasonsView.vue');

const RateGroupListView = () => import('@/views/management/RateGroupListView.vue');
const RateGroupDetailView = () => import('@/views/management/RateGroupDetailView.vue');
const RateGroupSegmentsView = () => import('@/views/management/RateGroupSegmentsView.vue');

const DemoComponentsView = () => import('@/views/demo-components/DemoComponentsView.vue');
const FullscreenMapView = () => import('@/views/FullscreenMap.vue');
const TransportDayPlannerView = () => import('@/views/transports/TransportDayPlannerView.vue');
const ProfileView = () => import('@/views/profile/ProfileView.vue');

const loginRoute = 'login';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: loginRoute,
      meta: {
        layout: AuthLayout,
        authRequired: false,
        translationName: 'page_titles.authentication.login',
      },
      component: LoginView,
    },
    {
      path: '/password-reset-request',
      name: 'password-reset-request',
      meta: {
        layout: AuthLayout,
        authRequired: false,
        translationName: 'page_titles.authentication.reset_password',
      },
      component: PasswordResetRequestView,
    },
    {
      path: '/password-reset-request-success',
      name: 'password-reset-request-success',
      meta: {
        layout: AuthLayout,
        authRequired: false,
        translationName: 'page_titles.authentication.reset_password_success',
      },
      component: PasswordResetRequestSuccess,
    },
    {
      path: '/create-new-password',
      name: 'create-new-password',
      meta: {
        layout: AuthLayout,
        authRequired: false,
        translationName: 'page_titles.authentication.new_password',
      },
      component: CreateNewPasswordView,
    },
    {
      path: '/no-access',
      name: 'no-access',
      meta: {
        layout: AuthLayout,
        authRequired: false,
        translationName: 'page_titles.no_access',
      },
      component: NoAccess,
    },
    {
      path: '/sso/callback',
      name: 'sso-callback',
      meta: {
        layout: AuthLayout,
        authRequired: false,
      },
      component: SsoCallbackView,
    },
    {
      path: '/consent/callback',
      name: 'consent-callback',
      meta: {
        layout: AuthLayout,
        authRequired: false,
      },
      component: ConsentCallbackView,
    },
    {
      path: '/',
      redirect: (to) => {
        const authStore = useAuthStore();
        return authStore.role === Role.Client || authStore.role === Role.ExternalUser
          ? '/assignment/list/plan'
          : '/trajectory/list/plan';
      },
    },
    {
      path: '/transport',
      name: 'transport',
      redirect: '/transport/list',
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        claims: ['transport.read'],
      },
      children: [
        {
          path: 'list',
          name: 'transport-list',
          redirect: '/transport/list/day-planner',
          meta: {
            layout: DefaultLayout,
            authRequired: true,
          },
          children: [
            {
              path: 'day-planner',
              name: 'transport-day-planner',
              component: TransportDayPlannerView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'day-planner',
                translationName: 'page_titles.transport.list.dayplanner',
              },
            },
            {
              path: 'plan',
              name: 'transport-list-plan',
              component: TransportListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'plan',
                statuses: [TransportStatus.ToPlan],
                translationName: 'page_titles.transport.list.plan',
              },
            },
            {
              path: 'planned',
              name: 'transport-list-planned',
              component: TransportListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'planned',
                statuses: [TransportStatus.Planned],
                translationName: 'page_titles.transport.list.planned',
              },
            },
            {
              path: 'transit',
              name: 'transport-list-transit',
              component: TransportListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'transit',
                statuses: [TransportStatus.InTransit],
                translationName: 'page_titles.transport.list.transit',
              },
            },
            {
              path: 'completed',
              name: 'transport-list-completed',
              component: TransportListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'completed',
                statuses: [TransportStatus.Completed],
                translationName: 'page_titles.transport.list.completed',
              },
            },
          ],
        },
        {
          path: ':id',
          name: 'transport-view',
          component: TransportView,
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            translationName: 'page_titles.transport.read',
          },
          beforeEnter: async (to) => {
            const transportRoutingStore = useTransportRoutingStore();
            const transportId = Number(to.params.id);
            await transportRoutingStore.getRouting(transportId);
          },
        },
      ],
    },
    {
      path: '/vehicle',
      name: 'vehicle',
      redirect: '/vehicle/list/all',
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        claims: ['vehicle.read'],
      },
      children: [
        {
          path: 'list',
          redirect: '/vehicle/list/all',
          children: [
            {
              path: 'all',
              name: 'vehicle-list-all',
              component: VehicleListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                translationName: 'page_titles.vehicle.list',
              },
            },
            {
              path: 'intermediate-location',
              name: 'vehicle-list-intermediate-location',
              component: VehicleListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                translationName: 'page_titles.vehicle.list',
                statuses: [VehicleStatus.OnIntermediateLocation],
              },
            },
            {
              path: 'in-storage',
              name: 'vehicle-list-in-storage',
              component: VehicleListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                translationName: 'page_titles.vehicle.list',
                statuses: [VehicleStatus.InStorage],
              },
              },
            ],
        },
        {
          path: ':id',
          name: 'vehicle-detail',
          component: VehicleDetailView,
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            translationName: 'page_titles.vehicle.read',
          },
        },
      ],
    },
    {
      path: '/assignment',
      name: 'assignment',
      redirect: '/assignment/list',
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        claims: ['assignment.read', 'assignment.read-own-assignment'],
      },
      children: [
        {
          path: 'list',
          name: 'assignment-list',
          redirect: '/assignment/list/plan',
          meta: {
            layout: DefaultLayout,
            authRequired: true,
          },
          children: [
            {
              path: 'plan',
              name: 'assignment-list-plan',
              component: AssignmentListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'plan',
                statuses: [AssignmentStatus.ToPlan],
                translationName: 'page_titles.assignment.list.plan',
              },
            },
            {
              path: 'planned',
              name: 'assignment-list-planned',
              component: AssignmentListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'planned',
                statuses: [AssignmentStatus.Planned],
                translationName: 'page_titles.assignment.list.planned',
              },
            },
            {
              path: 'transit',
              name: 'assignment-list-transit',
              component: AssignmentListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'transit',
                statuses: [AssignmentStatus.InTransit],
                translationName: 'page_titles.assignment.list.transit',
              },
            },
            {
              path: 'intermediate-location',
              name: 'assignment-list-intermediate-location',
              component: AssignmentListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'intermediate-location',
                statuses: [AssignmentStatus.JourneyWaypoint],
                translationName: 'page_titles.assignment.list.intermediate_location',
              },
            },
            {
              path: 'ready-for-promatie',
              name: 'assignment-list-ready-for-promatie',
              component: AssignmentListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'ready-for-promatie',
                statuses: [AssignmentStatus.Completed],
                invoiced: false,
                translationName: 'page_titles.assignment.list.ready_for_promatie',
                excludedRoles: [Role.Client, Role.ExternalUser],
              },
            },
            {
              path: 'completed',
              name: 'assignment-list-completed',
              component: AssignmentListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'completed',
                statuses: [AssignmentStatus.Completed, AssignmentStatus.Cancelled],
                invoiced: true,
                translationName: 'page_titles.assignment.list.completed',
              },
            },
            {
              path: 'received',
              name: 'assignment-list-received',
              component: AssignmentListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'received',
                statuses: [AssignmentStatus.Received],
                translationName: 'page_titles.assignment.list.received',
                excludedRoles: [Role.Client],
              },
            },
            {
              path: 'all',
              name: 'assignment-list-all',
              component: AssignmentListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'all',
                // Leeg = geen statusfilter: alle opdrachten ongeacht status (7418). Verplicht meegeven,
                // AssignmentListTable leest statuses.includes(...) op deze meta.
                statuses: [],
                defaultSortDescending: true,
                translationName: 'page_titles.assignment.list.all',
              },
            },
          ],
        },
        {
          path: ':id',
          name: 'assignment-view',
          component: AssignmentView,
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            translationName: 'page_titles.assignment.read',
          },
        },
        {
          path: 'create',
          name: 'assignment-create',
          component: AssignmentCreateView,
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            claims: ['assignment.create', 'assignment.create-assignment'],
            translationName: 'page_titles.assignment.create',
          },
        },
      ],
    },
    {
      path: '/trajectory',
      name: 'trajectory',
      redirect: 'trajectory/list',
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        claims: ['trajectory.read'],
      },
      children: [
        {
          path: 'list',
          name: 'trajectory-list',
          redirect: '/trajectory/list/plan',
          meta: {
            layout: DefaultLayout,
            authRequired: true,
          },
          children: [
            {
              path: 'plan',
              name: 'trajectory-list-plan',
              component: TrajectoryListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'plan',
                statuses: [TrajectoryStatus.ToPlan],
                showSplitAndMergeColumn: true,
                translationName: 'page_titles.trajectory.list.plan',
              },
            },
            {
              path: 'planned',
              name: 'trajectory-list-planned',
              component: TrajectoryListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'planned',
                statuses: [TrajectoryStatus.Planned],
                showSplitAndMergeColumn: false,
                translationName: 'page_titles.trajectory.list.planned',
              },
            },
            {
              path: 'transit',
              name: 'trajectory-list-transit',
              component: TrajectoryListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'transit',
                statuses: [TrajectoryStatus.InTransit],
                showSplitAndMergeColumn: false,
                translationName: 'page_titles.trajectory.list.transit',
              },
            },
            {
              path: 'completed',
              name: 'trajectory-list-completed',
              component: TrajectoryListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'completed',
                statuses: [TrajectoryStatus.Completed, TrajectoryStatus.Empty],
                showSplitAndMergeColumn: false,
                translationName: 'page_titles.trajectory.list.completed',
              },
            },
            {
              path: 'outsourced',
              name: 'trajectory-list-outsourced',
              component: TrajectoryListView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                postfix: 'outsourced',
                statuses: [TrajectoryStatus.Outsourced],
                showSplitAndMergeColumn: false,
                translationName: 'page_titles.trajectory.list.outsourced',
              },
            },
          ],
        },
      ],
    },
    {
      path: '/organization',
      name: 'organization',
      redirect: '/organization/users',
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        claims: ['organization.read'],
      },
      children: [
        {
          path: 'users',
          name: 'organization-users',
          component: UsersView,
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            claims: ['organization-users.read'],
            translationName: 'page_titles.organization.users',
          },
        },
        {
          path: 'settings',
          name: 'organization-settings',
          component: OrganizationSettingsView,
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            claims: ['organization-settings.read'],
            translationName: 'page_titles.organization.settings',
          },
        },
        {
          path: 'transport-reasons',
          name: 'transport-reasons',
          component: TransportReasonsView,
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            claims: ['organization-settings.read'],
            translationName: 'page_titles.organization.transport_reasons',
          },
        },
      ],
    },
    {
      path: '/management',
      name: 'management',
      redirect: '/management/rate-groups-sales',
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        claims: ['management-general.read'],
      },
      children: [
        {
          path: 'rate-groups-sales',
          name: 'rate-groups-sales',
          component: RateGroupListView,
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            claims: ['management-general.read'],
            translationName: 'page_header.management.sub_pages.rate_groups_sales',
            type: RateGroupType.SalesRate
          },
        },
        {
          path: 'rate-groups-purchasing',
          name: 'rate-groups-purchasing',
          component: RateGroupListView,
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            claims: ['management-general.read'],
            translationName: 'page_header.management.sub_pages.rate_groups_purchasing',
            type: RateGroupType.PurchasingRate
          },
        },
        {
          path: 'rate-group/:id',
          name: 'rate-group',
          redirect: to => to.path + '/detail',
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            claims: ['management-general.read'],
          },
          children: [
            {
              path: 'detail',
              name: 'rate-group-detail',
              component: RateGroupDetailView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                claims: ['management-general.read'],
                translationName: 'page_header.management.sub_pages.rate_group_details',
              }
            },
            {
              path: 'segments',
              name: 'rate-group-segments',
              component: RateGroupSegmentsView,
              meta: {
                layout: DefaultLayout,
                authRequired: true,
                claims: ['management-general.read'],
                translationName: 'page_header.management.sub_pages.rate_group_segments',
              },
            },
          ],
        }
      ],
    },
    {
      path: '/administration',
      name: 'administration',
      redirect: '/administration/organizations',
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        claims: ['administration-general.read'],
      },
      children: [
        {
          path: 'organizations',
          name: 'administration-organizations-list',
          component: AdministrationOrganizationView,
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            claims: ['administration-organizations.read'],
            translationName: 'page_titles.administration.organizations',
          },
        },
      ],
    },
    {
      path: '/relation',
      name: 'relation',
      redirect: '/relation/list',
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        claims: ['relation.read'],
      },
      children: [
        {
          path: 'list',
          name: 'relations-list',
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            claims: ['relation.read-list'],
            translationName: 'page_titles.relations',
          },
          component: RelationView,
        },
        {
          path: 'details/:id',
          name: 'relation-details',
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            claims: ['relation.read'],
            translationName: 'page_titles.relations',
          },
          component: RelationDetailView,
        },
      ],
    },
    {
      path: '/transport-vehicle',
      name: 'transport-vehicle',
      redirect: '/transport-vehicle/list',
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        claims: ['transport-vehicle.read'],
      },
      children: [
        {
          path: 'list',
          name: 'transport-vehicle-list',
          component: TransportVehicleListView,
          meta: {
            layout: DefaultLayout,
            authRequired: true,
            claims: ['transport-vehicle.read'],
            translationName: 'page_titles.transport_vehicle.list',
          },
        },
      ],
    },
    // Demo page
    {
      path: '/demo-components',
      name: 'demo-components',
      component: DemoComponentsView,
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        translationName: 'page_titles.authentication.login',
      },
    },
    {
      path: '/fullscreen-map',
      name: 'fullscreen-map',
      component: FullscreenMapView,
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        translationName: 'page_titles.map',
      },
    },
    {
      path: '/profile',
      name: 'profile',
      component: ProfileView,
      meta: {
        layout: DefaultLayout,
        authRequired: true,
        translationName: 'profile.title',
      },
    },
    // Wildcard route
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      meta: {
        layout: DefaultLayout,
        authRequired: false,
        translationName: 'page_titles.not_found',
      },
      component: NotFoundView,
    },
  ],
});

router.beforeEach(async (to, from) => {
  //Cancel every outstanding request when navigating
  const axiosStore = useAxiosStore();

  //Prevent aborting requests when changing n-tabs tab
  if (from.name != to.name) {
    axiosStore.abort('Navigation');
  }

  const navigationStore = useNavigationStore();
  const authStore = useAuthStore();

  // Ensure cookie-based session is loaded before enforcing auth/claims.
  await authStore.ensureHydrated();

  //Prevent without authentication to a route that requires authentication, redirect to login
  if (!authStore.isAuthenticated() && to?.meta?.authRequired == true) {
    navigationStore.requestedRoute = to.path;
    return { name: loginRoute };
  }

  // If the user redirects to /login when already logged in, return to home
  if (to?.path === '/login' && authStore.isAuthenticated() && !to.query.ssoError) {
    return { path: '/' };
  }

  // check claims
  if (to?.meta?.claims && !authStore.hasAtLeastOneClaim(to?.meta?.claims as string[])) {
    return { name: 'no-access' };
  }

  // check excluded roles
  const excludedRoles = to?.meta?.excludedRoles as Role[] | undefined;
  if (excludedRoles && excludedRoles.includes(authStore.role)) {
    return { name: 'no-access' };
  }
});

// After each route connect to SignalR and make sure organization feature toggles are set
router.afterEach(async (to, from) => {
  const navigationStore = useNavigationStore();
  const excludedPaths = ['/login'];

  navigationStore.currentPageTranslationLabel = to.meta?.translationName as string;
  navigationStore.routeName = to.name?.toString() ?? '';

  if (excludedPaths.includes(to.path)) {
    return;
  }
});

export default router;
